import { defineCatalogue } from '../../../i18n';

const pt = {
  title: 'Assistente IA',
  'refusal.no_reading.title': 'sem leitura no período',
  'refusal.tool_limit.title': 'pergunta ampla demais para uma resposta',
  'refusal.unknown_tool.title': 'isto ainda não é algo que sei consultar',
  'refusal.would_have_read': 'Seria lido: {items}',
  'tool.get_posts': 'publicações',
  'tool.get_post_metrics': 'métricas das publicações',
  'tool.get_post_reach': 'alcance da publicação',
  'tool.get_ranked_posts': 'publicações com sentimento negativo',
  'tool.get_amplification': 'amplificação',
  'tool.get_comments': 'comentários',
  'tool.get_comment_terms': 'termos dos comentários',
  'tool.get_sentiment_series': 'sentimento',
  'tool.get_composition': 'composição da audiência',
  'tool.get_campaign_composition': 'composição da audiência da campanha',
  'tool.get_localities': 'localidades',
  'tool.get_territory': 'território e eleitorado',
  'tool.get_topics': 'temas',
  'tool.run_query': 'consulta SQL do assistente',
  'tool.get_counts': 'contagem por grupo',
  'tool.render_chart': 'gráfico',
  'tool.fallback': 'dados da campanha',
  'interrupted.title': 'a resposta foi cortada no meio',
  'interrupted.description': 'A pergunta continua aqui, sem perda.',
  'interrupted.askAgain': 'Perguntar de novo',
  'evidence.show': 'Ver os dados usados ({count})',
  'failed.title': 'o assistente não respondeu',
  'card.post_metrics.title': 'Métricas do post',
  'card.amplification.title': 'Quem amplificou',
  'card.comment_terms.title': 'Termos nos comentários',
  'card.comments.title': 'Comentários',
  'card.composition.title': 'Composição do público',
  'card.composition.unidentified': 'Não identificado',
  'card.sentiment.title': 'Sentimento',
  'card.sentiment.titleByLevel': 'Sentimento · {level}',
  'card.posts.empty': 'Nenhum post correspondeu à pergunta.',
  'card.query.title': 'Resultado da consulta',
  'card.query.empty': 'A consulta não devolveu linhas.',
  'card.query.truncated': 'Mostrando as primeiras {count} linhas.',
  'card.chart.source': 'Fonte: {tool}',
  'card.chart.sourceSql': 'Gráfico montado pelo assistente a partir de uma consulta SQL',
  'card.chart.sourceWindow': 'Fonte: {tool}, {window}',
  'card.chart.title': 'Gráfico',
  'card.chart.empty': 'Sem dados para desenhar.',
  'provenance.openInSentiment': 'Abrir em Sentimento',
  'start.heading': 'O que você quer saber sobre {campaign}?',
  'start.subtitle':
    'Pergunte em português. A resposta vem do que a campanha já coletou, com o gráfico e as contas que a sustentam.',
  'composer.placeholder': 'Pergunte sobre esta campanha',
  'composer.send': 'Enviar pergunta',
  'author.name': 'SocialSrc',
  'answer.thinking': 'consultando…',
  'scope.post.clear': 'Remover publicação do escopo',
  'scope.post.unknown':
    'A publicação indicada não foi encontrada; a pergunta segue sobre toda a campanha.',
  'reset.trigger': 'Nova conversa',
  'reset.title': 'Começar uma nova conversa?',
  'reset.description':
    'As perguntas e respostas desta conversa saem da tela e não podem ser recuperadas. Uma resposta ainda em andamento é interrompida.',
  'reset.confirm': 'Nova conversa',
  'scroll.latest': 'Ir para a mensagem mais recente',
  'followups.group': 'Perguntas de acompanhamento',
} satisfies Record<string, string>;

export type AssistantMessageKey = keyof typeof pt;

export function isAssistantMessageKey(key: string): key is AssistantMessageKey {
  return key in pt;
}

export const assistantCatalogue = defineCatalogue(pt, {
  title: 'AI Assistant',
  'refusal.no_reading.title': 'no reading in the period',
  'refusal.tool_limit.title': 'too broad a question for one answer',
  'refusal.unknown_tool.title': "this isn't something I can look up yet",
  'refusal.would_have_read': 'Would have read: {items}',
  'tool.get_posts': 'posts',
  'tool.get_post_metrics': 'post metrics',
  'tool.get_post_reach': 'post reach',
  'tool.get_ranked_posts': 'negatively received posts',
  'tool.get_amplification': 'amplification',
  'tool.get_comments': 'comments',
  'tool.get_comment_terms': 'comment terms',
  'tool.get_sentiment_series': 'sentiment',
  'tool.get_composition': 'audience composition',
  'tool.get_campaign_composition': 'campaign audience composition',
  'tool.get_localities': 'localities',
  'tool.get_territory': 'territory and electorate',
  'tool.get_topics': 'topics',
  'tool.run_query': "the assistant's SQL query",
  'tool.get_counts': 'group count',
  'tool.render_chart': 'chart',
  'tool.fallback': 'campaign data',
  'interrupted.title': 'the answer was cut mid-stream',
  'interrupted.description': 'The question is still here, nothing lost.',
  'interrupted.askAgain': 'Ask again',
  'evidence.show': 'See the data used ({count})',
  'failed.title': 'the assistant did not answer',
  'card.post_metrics.title': 'Post metrics',
  'card.amplification.title': 'Who amplified',
  'card.comment_terms.title': 'Comment terms',
  'card.comments.title': 'Comments',
  'card.composition.title': "Audience's composition",
  'card.composition.unidentified': 'Not identified',
  'card.sentiment.title': 'Sentiment',
  'card.sentiment.titleByLevel': 'Sentiment · {level}',
  'card.posts.empty': 'No post matched the question.',
  'card.query.title': 'Query result',
  'card.query.empty': 'The query returned no rows.',
  'card.query.truncated': 'Showing the first {count} rows.',
  'card.chart.source': 'Source: {tool}',
  'card.chart.sourceSql': 'Chart built by the assistant from an SQL query',
  'card.chart.sourceWindow': 'Source: {tool}, {window}',
  'card.chart.title': 'Chart',
  'card.chart.empty': 'No data to draw.',
  'provenance.openInSentiment': 'Open in Sentiment',
  'start.heading': 'What do you want to know about {campaign}?',
  'start.subtitle':
    'Ask in your own words. The answer comes from what the campaign has already collected, with the chart and the accounts behind it.',
  'composer.placeholder': 'Ask about this campaign',
  'composer.send': 'Send question',
  'author.name': 'SocialSrc',
  'answer.thinking': 'thinking…',
  'scope.post.clear': 'Remove the post from scope',
  'scope.post.unknown':
    'The post named in the link was not found; the question stays scoped to the whole campaign.',
  'reset.trigger': 'New conversation',
  'reset.title': 'Start a new conversation?',
  'reset.description':
    'The questions and answers in this conversation leave the screen and cannot be recovered. An answer still streaming is stopped.',
  'reset.confirm': 'New conversation',
  'scroll.latest': 'Jump to the latest message',
  'followups.group': 'Follow-up questions',
});

export const at = assistantCatalogue.t;
